import * as ExcelJS from "exceljs";
import { Chart, ArcElement, DoughnutController, Legend, Tooltip } from "chart.js";
import { SubZoneType, ValeurGroupBy, ValeurStockDetail } from "../../models/data.model";

/** Libellé affiché pour chaque catégorie de sous-zone d'entrepôt dans l'export
 *  Excel (mode `byEntrepot`). Doit rester aligné avec `valeur-par-famille-
 *  dialog.component.ts` (`SUB_ZONE_LABELS`). */
const SUB_ZONE_LABELS: Record<Exclude<SubZoneType, null>, string> = {
  pdc: "Pieds de chaîne",
  nc: "Zone non conforme",
  expedition: "Zone d'expédition",
  reception: "Zone de réception",
  quarantaine: "Zone de quarantaine",
  transfert: "Zone de transfert",
  dechargement: "Zone de déchargement",
  autre: "Autres sous-zones",
};
const SUB_ZONE_ORDER: Array<Exclude<SubZoneType, null>> = [
  "pdc",
  "nc",
  "expedition",
  "reception",
  "quarantaine",
  "transfert",
  "dechargement",
  "autre",
];
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";

// Register chart.js components needed for offscreen rendering
Chart.register(ArcElement, DoughnutController, Legend, Tooltip);

const CHART_COLORS = [
  "#4e79a7", "#f28e2b", "#e15759", "#76b7b2", "#59a14f",
  "#edc948", "#b07aa1", "#ff9da7", "#9c755f", "#bab0ac",
  "#8cd17d", "#a0cbe8",
];

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  groupBg: "FFE8EDF3",
  groupFont: "FF1B2A4A",
  articleBg: "FFF5F7FA",
  border: "FFD0D5DD",
  lotFont: "FF667085",
  lotBorder: "FFE4E7EC",
  statGreen: "FFE6FFF5",
  statPurple: "FFF0E6FF",
  statOrange: "FFFFF0E0",
  titleColor: "FF1B2A4A",
};

// ─────────────────────────────────────────────────────────────────────────────
// Offscreen Chart.js → PNG base64
// ─────────────────────────────────────────────────────────────────────────────
function renderDoughnutPng(labels: string[], values: number[]): string {
  const canvas = document.createElement("canvas");
  canvas.width = 500;
  canvas.height = 500;
  const ctx = canvas.getContext("2d")!;

  const chart = new Chart(ctx, {
    type: "doughnut",
    data: {
      labels,
      datasets: [{
        data: values,
        backgroundColor: CHART_COLORS.slice(0, labels.length),
        borderWidth: 1,
        borderColor: "#fff",
      }],
    },
    options: {
      responsive: false,
      animation: false,
      plugins: {
        legend: { display: true, position: "bottom", labels: { font: { size: 11 }, padding: 8 } },
        tooltip: { enabled: false },
      },
    },
  });

  const dataUrl = canvas.toDataURL("image/png");
  chart.destroy();
  // Strip the data:image/png;base64, prefix
  return dataUrl.replace(/^data:image\/png;base64,/, "");
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) {
    c--;
    s = String.fromCharCode(65 + (c % 26)) + s;
    c = Math.floor(c / 26);
  }
  return s;
}

function cellRef(col: number, row: number): string {
  return `${colLetter(col)}${row}`;
}

function headerStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}

function statStyle(cell: ExcelJS.Cell, bg: string) {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
  cell.border = {
    top: { style: "thin", color: { argb: C.border } },
    bottom: { style: "thin", color: { argb: C.border } },
    left: { style: "thin", color: { argb: C.border } },
    right: { style: "thin", color: { argb: C.border } },
  };
}

function groupStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 10, color: { argb: C.groupFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.groupBg } };
  cell.border = {
    bottom: { style: "thin", color: { argb: C.border } },
    top: { style: "thin", color: { argb: C.border } },
  };
}

function articleStyle(cell: ExcelJS.Cell) {
  cell.font = { size: 9, italic: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.articleBg } };
  cell.border = { bottom: { style: "hair", color: { argb: C.border } } };
}

/** Style intermédiaire pour les lignes "sous-famille" en mode `byFamille`.
 *  Entre le groupe (famille) et les articles. Contraste léger sur le fond
 *  article pour signaler la hiérarchie visuelle. */
function subGroupStyle(cell: ExcelJS.Cell) {
  cell.font = { bold: true, size: 9.5, color: { argb: C.groupFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFEEF2F8" } };
  cell.border = {
    bottom: { style: "thin", color: { argb: C.border } },
  };
}

function lotStyle(cell: ExcelJS.Cell) {
  cell.font = { size: 9, color: { argb: C.lotFont } };
  cell.border = { bottom: { style: "hair", color: { argb: C.lotBorder } } };
}

// ─────────────────────────────────────────────────────────────────────────────
// Build one sheet
// ─────────────────────────────────────────────────────────────────────────────
// Column layout:
//  A=Groupe  B=Article  C=N°Lot  D=DDM/DLC  E=Entrepôt
//  F=Quantité  G=Unité  H=Coût unit.(€)  I=Total(€)  J=%
const COL = { grp: 1, art: 2, lot: 3, ddm: 4, ent: 5, qty: 6, unit: 7, cost: 8, total: 9, pct: 10 };
const HEADER_ROW = 5;
const DATA_START = 6;
const COL_WIDTHS = [22, 25, 16, 14, 18, 12, 8, 14, 14, 8];
const HEADERS = ["Groupe", "Article", "N° Lot", "DDM/DLC", "Entrepôt", "Quantité", "Unité", "Coût unit. (€)", "Total (€)", "%"];

function buildSheet(
  wb: ExcelJS.Workbook,
  sheetName: string,
  groupByKey: ValeurGroupBy,
  items: { denomination: string; total: number }[],
  details: ValeurStockDetail[],
  grandTotal: number,
) {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: "frozen", ySplit: HEADER_ROW }],
  });

  const sorted = [...items].filter((d) => d.total > 0).sort((a, b) => b.total - a.total);

  // ── Column widths ──
  COL_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  // ── Title ── (laisse la dernière colonne pour le branding JustUp)
  ws.mergeCells("A1:I1");
  const titleCell = ws.getCell("A1");
  titleCell.value = `Valeur stock — ${sheetName}`;
  titleCell.font = { bold: true, size: 16, color: { argb: C.titleColor } };
  titleCell.alignment = { vertical: "middle" };
  ws.getRow(1).height = 30;

  // ── Headers ──
  HEADERS.forEach((h, i) => {
    const cell = ws.getCell(HEADER_ROW, i + 1);
    cell.value = h;
    headerStyle(cell);
  });
  ws.getRow(HEADER_ROW).height = 24;
  ws.autoFilter = { from: { row: HEADER_ROW, column: 1 }, to: { row: HEADER_ROW, column: 10 } };

  // ── Data rows ──
  // We need to track rows for formulas: group total = SUM(article totals), article total = SUM(lot totals)
  let row = DATA_START;
  const groupRowNumbers: number[] = []; // to reference in stats

  // Helper interne — écrit une ligne article + ses lots, retourne la ref de
  // la cellule total de l'article (pour le SUM du groupe parent).
  const writeArticle = (
    artName: string,
    lots: ValeurStockDetail[],
    artTotal: number,
  ): string => {
    const artRowNum = row;
    row++; // réserve la ligne article

    const lotTotalCells: string[] = [];
    const sortedLots = [...lots].sort((a, b) => b.total - a.total);
    for (const lot of sortedLots) {
      const r = ws.getRow(row);
      r.getCell(COL.lot).value = lot.lotNumero;
      r.getCell(COL.ddm).value = lot.dluo ?? "";
      r.getCell(COL.ent).value = lot.entrepot;
      r.getCell(COL.qty).value = lot.quantite;
      r.getCell(COL.unit).value = lot.unite;
      r.getCell(COL.cost).value = lot.coutUnitaire;
      r.getCell(COL.total).value = {
        formula: `${cellRef(COL.qty, row)}*${cellRef(COL.cost, row)}`,
        result: lot.total,
      } as any;

      for (let c = 1; c <= 10; c++) lotStyle(r.getCell(c));
      r.getCell(COL.qty).numFmt = "#,##0.00";
      r.getCell(COL.cost).numFmt = '#,##0.00\\ "€"';
      r.getCell(COL.total).numFmt = '#,##0.00\\ "€"';

      lotTotalCells.push(cellRef(COL.total, row));
      row++;
    }

    const artR = ws.getRow(artRowNum);
    artR.getCell(COL.art).value = artName;
    if (lotTotalCells.length > 0) {
      artR.getCell(COL.total).value = {
        formula: `SUM(${lotTotalCells.join(",")})`,
        result: artTotal,
      } as any;
    } else {
      artR.getCell(COL.total).value = artTotal;
    }
    for (let c = 1; c <= 10; c++) articleStyle(artR.getCell(c));
    artR.getCell(COL.total).numFmt = '#,##0.00\\ "€"';

    return cellRef(COL.total, artRowNum);
  };

  for (const group of sorted) {
    const groupRowNum = row;
    groupRowNumbers.push(groupRowNum);
    row++; // réserve la ligne groupe (famille / entrepôt / article)

    // Filtrage des détails liés à ce groupe
    const groupDetails = details.filter((d) => {
      if (groupByKey === "byEntrepot") return d.entrepot === group.denomination;
      if (groupByKey === "byFamille") return d.famille === group.denomination;
      if (groupByKey === "bySousFamille") return d.sousFamille === group.denomination;
      if (groupByKey === "byArticle") return d.article === group.denomination;
      return false;
    });

    const articleTotalCells: string[] = [];

    if (groupByKey === "byFamille") {
      // Mode famille : on insère le niveau SOUS-FAMILLE pour les articles
      // qui en ont une, et on place les articles orphelins (sans
      // sous-famille) DIRECTEMENT sous la famille — pas de wrapper "Non
      // classé" intermédiaire.
      const subMap = new Map<string, Map<string, ValeurStockDetail[]>>();
      const orphanMap = new Map<string, ValeurStockDetail[]>();
      for (const d of groupDetails) {
        if (!d.sousFamille) {
          if (!orphanMap.has(d.article)) orphanMap.set(d.article, []);
          orphanMap.get(d.article)!.push(d);
        } else {
          if (!subMap.has(d.sousFamille)) subMap.set(d.sousFamille, new Map());
          const artMap = subMap.get(d.sousFamille)!;
          if (!artMap.has(d.article)) artMap.set(d.article, []);
          artMap.get(d.article)!.push(d);
        }
      }

      const subArr = [...subMap.entries()]
        .map(([sfDenom, artMap]) => {
          const articles = [...artMap.entries()]
            .map(([name, lots]) => ({
              name,
              lots,
              total: lots.reduce((s, l) => s + l.total, 0),
            }))
            .sort((a, b) => b.total - a.total);
          return {
            denomination: sfDenom,
            articles,
            total: articles.reduce((s, a) => s + a.total, 0),
          };
        })
        .sort((a, b) => b.total - a.total);

      for (const sf of subArr) {
        const sfRowNum = row;
        row++; // réserve la ligne sous-famille (remplie après les articles)

        const sfArticleCells: string[] = [];
        for (const art of sf.articles) {
          sfArticleCells.push(writeArticle(art.name, art.lots, art.total));
        }

        // Ligne sous-famille — préfixée " ↳ " pour signaler l'imbrication
        const sfR = ws.getRow(sfRowNum);
        sfR.getCell(COL.grp).value = `  ↳ ${sf.denomination}`;
        if (sfArticleCells.length > 0) {
          sfR.getCell(COL.total).value = {
            formula: `SUM(${sfArticleCells.join(",")})`,
            result: sf.total,
          } as any;
        } else {
          sfR.getCell(COL.total).value = sf.total;
        }
        for (let c = 1; c <= 10; c++) subGroupStyle(sfR.getCell(c));
        sfR.getCell(COL.total).numFmt = '#,##0.00\\ "€"';

        articleTotalCells.push(cellRef(COL.total, sfRowNum));
      }

      // Articles orphelins (sans sous-famille) → écrits directement sous la
      // famille. Leur total contribue au SUM de la famille au même niveau
      // que les sous-familles.
      const orphanArr = [...orphanMap.entries()]
        .map(([name, lots]) => ({ name, lots, total: lots.reduce((s, l) => s + l.total, 0) }))
        .sort((a, b) => b.total - a.total);
      for (const art of orphanArr) {
        articleTotalCells.push(writeArticle(art.name, art.lots, art.total));
      }
    } else if (groupByKey === "byEntrepot") {
      // Mode entrepôt : on insère le niveau CATÉGORIE DE SOUS-ZONE (PDC,
      // Zone NC, expé…) sous l'entrepôt parent. Le bucket "Stock direct"
      // (subZoneType === null) est rendu en dernier sous-groupe pour
      // refléter l'ordre du dialog (sous-zones d'abord, stock direct en
      // dessous, comme demandé).
      const subZoneMap = new Map<Exclude<SubZoneType, null>, Map<string, ValeurStockDetail[]>>();
      const directMap = new Map<string, ValeurStockDetail[]>();
      for (const d of groupDetails) {
        if (d.subZoneType === null) {
          if (!directMap.has(d.article)) directMap.set(d.article, []);
          directMap.get(d.article)!.push(d);
        } else {
          if (!subZoneMap.has(d.subZoneType)) subZoneMap.set(d.subZoneType, new Map());
          const artMap = subZoneMap.get(d.subZoneType)!;
          if (!artMap.has(d.article)) artMap.set(d.article, []);
          artMap.get(d.article)!.push(d);
        }
      }

      const subZoneArr = SUB_ZONE_ORDER.flatMap((cat) => {
        const artMap = subZoneMap.get(cat);
        if (!artMap || artMap.size === 0) return [];
        const articles = [...artMap.entries()]
          .map(([name, lots]) => ({
            name,
            lots,
            total: lots.reduce((s, l) => s + l.total, 0),
          }))
          .sort((a, b) => b.total - a.total);
        return [{
          denomination: SUB_ZONE_LABELS[cat],
          articles,
          total: articles.reduce((s, a) => s + a.total, 0),
        }];
      });

      // Sous-groupe "Stock direct" rendu en DERNIER si présent.
      if (directMap.size > 0) {
        const directArticles = [...directMap.entries()]
          .map(([name, lots]) => ({
            name,
            lots,
            total: lots.reduce((s, l) => s + l.total, 0),
          }))
          .sort((a, b) => b.total - a.total);
        subZoneArr.push({
          denomination: "Stock direct",
          articles: directArticles,
          total: directArticles.reduce((s, a) => s + a.total, 0),
        });
      }

      for (const sz of subZoneArr) {
        const szRowNum = row;
        row++; // réserve la ligne sous-zone

        const szArticleCells: string[] = [];
        for (const art of sz.articles) {
          szArticleCells.push(writeArticle(art.name, art.lots, art.total));
        }

        // Ligne sous-zone — préfixée " ↳ " pour signaler l'imbrication
        const szR = ws.getRow(szRowNum);
        szR.getCell(COL.grp).value = `  ↳ ${sz.denomination}`;
        if (szArticleCells.length > 0) {
          szR.getCell(COL.total).value = {
            formula: `SUM(${szArticleCells.join(",")})`,
            result: sz.total,
          } as any;
        } else {
          szR.getCell(COL.total).value = sz.total;
        }
        for (let c = 1; c <= 10; c++) subGroupStyle(szR.getCell(c));
        szR.getCell(COL.total).numFmt = '#,##0.00\\ "€"';

        articleTotalCells.push(cellRef(COL.total, szRowNum));
      }
    } else {
      // Modes sous-famille / article : 3 niveaux groupe → article → lot.
      const articleMap = new Map<string, ValeurStockDetail[]>();
      for (const d of groupDetails) {
        const artKey = groupByKey === "byArticle" ? d.entrepot : d.article;
        if (!articleMap.has(artKey)) articleMap.set(artKey, []);
        articleMap.get(artKey)!.push(d);
      }
      const articlesArr = [...articleMap.entries()]
        .map(([name, lots]) => ({ name, lots, total: lots.reduce((s, l) => s + l.total, 0) }))
        .sort((a, b) => b.total - a.total);
      for (const art of articlesArr) {
        articleTotalCells.push(writeArticle(art.name, art.lots, art.total));
      }
    }

    // Ligne groupe (famille / entrepôt / sous-famille / article) — SUM des
    // sous-familles (mode famille) ou directement des articles.
    const grpR = ws.getRow(groupRowNum);
    grpR.getCell(COL.grp).value = group.denomination;
    if (articleTotalCells.length > 0) {
      grpR.getCell(COL.total).value = {
        formula: `SUM(${articleTotalCells.join(",")})`,
        result: group.total,
      } as any;
    } else {
      grpR.getCell(COL.total).value = group.total;
    }
    for (let c = 1; c <= 10; c++) groupStyle(grpR.getCell(c));
    grpR.getCell(COL.total).numFmt = '#,##0.00\\ "€"';
  }

  const lastDataRow = row - 1;

  // ── Grand total row ──
  row++; // blank separator
  const grandTotalRow = row;
  ws.mergeCells(grandTotalRow, 1, grandTotalRow, COL.cost);
  const gtLabelCell = ws.getCell(grandTotalRow, 1);
  gtLabelCell.value = "TOTAL GÉNÉRAL";
  gtLabelCell.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtLabelCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtLabelCell.alignment = { horizontal: "right", vertical: "middle" };

  // Grand total = SUM of group totals
  const gtCell = ws.getCell(grandTotalRow, COL.total);
  if (groupRowNumbers.length > 0) {
    const refs = groupRowNumbers.map((r) => cellRef(COL.total, r)).join(",");
    gtCell.value = { formula: `SUM(${refs})`, result: grandTotal } as any;
  } else {
    gtCell.value = grandTotal;
  }
  gtCell.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtCell.numFmt = '#,##0.00\\ "€"';

  const gtPctCell = ws.getCell(grandTotalRow, COL.pct);
  gtPctCell.value = 1;
  gtPctCell.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtPctCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtPctCell.numFmt = "0.0%";
  ws.getRow(grandTotalRow).height = 26;

  const gtRef = cellRef(COL.total, grandTotalRow); // absolute ref for %

  // ── Now fill in % formulas for all group/article/lot rows ──
  for (let r = DATA_START; r <= lastDataRow; r++) {
    const totalCellVal = ws.getCell(r, COL.total).value;
    // Only add % if this row has a total (formula or value)
    if (totalCellVal !== null && totalCellVal !== undefined && totalCellVal !== "") {
      ws.getCell(r, COL.pct).value = {
        formula: `IF(${gtRef}=0,0,${cellRef(COL.total, r)}/${gtRef})`,
        result: grandTotal > 0 ? ((ws.getCell(r, COL.total).value as any)?.result ?? 0) / grandTotal : 0,
      } as any;
      ws.getCell(r, COL.pct).numFmt = "0.0%";
    }
  }

  // ── Stats section (row 3) with formulas ──
  ws.mergeCells("A3:C3");
  ws.mergeCells("D3:F3");
  ws.mergeCells("G3:I3");

  // Stat 1: Valeur totale — reference grand total cell
  const s1 = ws.getCell("A3");
  s1.value = { formula: `"Valeur totale : "&TEXT(${gtRef},"# ##0 €")`, result: `Valeur totale : ${formatEur(grandTotal)}` } as any;
  statStyle(s1, C.statGreen);

  // Stat 2: Count
  const groupCount = sorted.length;
  const s2 = ws.getCell("D3");
  s2.value = `${groupCount} ${sheetName.toLowerCase()}`;
  statStyle(s2, C.statPurple);

  // Stat 3: Pareto 80% — computed since it needs cumulative logic
  let cum = 0;
  let p80 = 0;
  for (const item of sorted) { cum += item.total; p80++; if (cum >= grandTotal * 0.8) break; }
  const s3 = ws.getCell("G3");
  s3.value = `Concentration 80% : ${p80} / ${groupCount}`;
  statStyle(s3, C.statOrange);
  ws.getRow(3).height = 28;

  // ── Chart summary table (col L-N) with formulas ──
  const chartCol = 12; // L
  const chartHeaders = ["Répartition", "Valeur (€)", "%"];
  chartHeaders.forEach((h, i) => {
    const cell = ws.getCell(HEADER_ROW, chartCol + i);
    cell.value = h;
    headerStyle(cell);
  });
  ws.getColumn(chartCol).width = 22;
  ws.getColumn(chartCol + 1).width = 16;
  ws.getColumn(chartCol + 2).width = 8;

  // Write chart data — reference group total cells directly
  const chartItems = sorted.slice(0, 12);
  const rest = sorted.slice(12);

  chartItems.forEach((item, i) => {
    const r = HEADER_ROW + 1 + i;
    const grpRowNum = groupRowNumbers[i];
    ws.getCell(r, chartCol).value = item.denomination;
    ws.getCell(r, chartCol).font = { size: 9 };
    // Formula: reference the group total
    ws.getCell(r, chartCol + 1).value = { formula: cellRef(COL.total, grpRowNum), result: item.total } as any;
    ws.getCell(r, chartCol + 1).numFmt = '#,##0.00\\ "€"';
    ws.getCell(r, chartCol + 1).font = { size: 9 };
    ws.getCell(r, chartCol + 2).value = { formula: `${cellRef(chartCol + 1, r)}/${gtRef}`, result: grandTotal > 0 ? item.total / grandTotal : 0 } as any;
    ws.getCell(r, chartCol + 2).numFmt = "0.0%";
    ws.getCell(r, chartCol + 2).font = { size: 9 };
  });

  // "Autres" row if needed
  if (rest.length > 0) {
    const autresRow = HEADER_ROW + 1 + chartItems.length;
    const restGroupRefs = groupRowNumbers.slice(12).map((r) => cellRef(COL.total, r));
    ws.getCell(autresRow, chartCol).value = "Autres";
    ws.getCell(autresRow, chartCol).font = { size: 9, italic: true };
    ws.getCell(autresRow, chartCol + 1).value = { formula: `SUM(${restGroupRefs.join(",")})`, result: rest.reduce((s, d) => s + d.total, 0) } as any;
    ws.getCell(autresRow, chartCol + 1).numFmt = '#,##0.00\\ "€"';
    ws.getCell(autresRow, chartCol + 1).font = { size: 9 };
    ws.getCell(autresRow, chartCol + 2).value = { formula: `${cellRef(chartCol + 1, autresRow)}/${gtRef}`, result: 0 } as any;
    ws.getCell(autresRow, chartCol + 2).numFmt = "0.0%";
    ws.getCell(autresRow, chartCol + 2).font = { size: 9 };
  }

  // ── Doughnut chart image ──
  const chartLabels = chartItems.map((d) => d.denomination);
  const chartValues = chartItems.map((d) => d.total);
  if (rest.length > 0) {
    chartLabels.push("Autres");
    chartValues.push(rest.reduce((s, d) => s + d.total, 0));
  }

  if (chartLabels.length > 0) {
    try {
      const pngBase64 = renderDoughnutPng(chartLabels, chartValues);
      const imageId = wb.addImage({ base64: pngBase64, extension: "png" });
      const chartImageRow = HEADER_ROW + 1 + chartLabels.length + 1;
      ws.addImage(imageId, {
        tl: { col: chartCol - 1, row: chartImageRow } as any,
        ext: { width: 420, height: 420 },
      });
    } catch {
      // Chart rendering failed (e.g. no canvas support) — skip silently
    }
  }
}

function formatEur(n: number): string {
  return new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
}

// ─────────────────────────────────────────────────────────────────────────────
// Public export
// ─────────────────────────────────────────────────────────────────────────────
export async function exportValeurStockExcel(
  data: {
    byFamille: { denomination: string; total: number }[];
    byArticle: { denomination: string; total: number }[];
    byEntrepot: { denomination: string; total: number }[];
    details: ValeurStockDetail[];
    total: number;
  },
): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const sheets: { key: ValeurGroupBy; label: string; items: { denomination: string; total: number }[] }[] = [
    { key: "byFamille", label: "Familles", items: data.byFamille },
    { key: "byArticle", label: "Articles", items: data.byArticle },
    { key: "byEntrepot", label: "Entrepôts", items: data.byEntrepot.filter((d) => d.total > 0) },
  ];

  for (const sheet of sheets) {
    buildSheet(wb, sheet.label, sheet.key, sheet.items, data.details, data.total);
    await addJustupBranding(wb, wb.getWorksheet(sheet.label)!, 10);
  }

  addRawDataSheet(wb, data.details);

  const buffer = await wb.xlsx.writeBuffer() as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = `valeur-stock-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);

  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();

  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 10000);
}

function rawHeaderStyleFn(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle" };
}

function addRawDataSheet(wb: ExcelJS.Workbook, details: ValeurStockDetail[]): void {
  const RAW_HEADERS = [
    "Famille",
    "Sous-famille",
    "Article",
    "N° Lot",
    "DDM/DLC",
    "Entrepôt",
    "Quantité",
    "Unité",
    "Coût unit. (€)",
    "Total (€)",
  ];
  const RAW_WIDTHS = [22, 22, 28, 16, 14, 18, 12, 10, 14, 14];

  const ws = wb.addWorksheet("Données brutes", {
    views: [{ state: "frozen", ySplit: 1, showGridLines: true }],
  });

  RAW_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  RAW_HEADERS.forEach((h, i) => {
    const c = ws.getCell(1, i + 1);
    c.value = h;
    rawHeaderStyleFn(c);
  });
  ws.getRow(1).height = 22;
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: RAW_HEADERS.length } };

  const eurFmt = '#,##0.00\\ "€"';
  let r = 1;
  for (const d of details) {
    r++;
    const row = ws.getRow(r);
    row.getCell(1).value = d.famille;
    row.getCell(2).value = d.sousFamille ?? "";
    row.getCell(3).value = d.article;
    row.getCell(4).value = d.lotNumero;
    row.getCell(5).value = d.dluo ?? "";
    row.getCell(6).value = d.entrepot;
    row.getCell(7).value = d.quantite;
    row.getCell(7).numFmt = "#,##0.00";
    row.getCell(8).value = d.unite;
    row.getCell(9).value = d.coutUnitaire;
    row.getCell(9).numFmt = eurFmt;
    row.getCell(9).alignment = { horizontal: "right" };
    row.getCell(10).value = d.total;
    row.getCell(10).numFmt = eurFmt;
    row.getCell(10).alignment = { horizontal: "right" };
  }
}
